import { dealPlayerHand, canPlayOnFoundation, canPlayOnTableau } from './deck.js';

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function createInitialGameState(playerName, difficulty) {
  const playerHand = dealPlayerHand(0);
  const aiHand = dealPlayerHand(1);
  // Convert tableau single cards to stacks (arrays)
  playerHand.tableau = playerHand.tableau.map(card => card ? [card] : []);
  aiHand.tableau = aiHand.tableau.map(card => card ? [card] : []);
  return {
    phase: 'playing',
    winner: null,
    foundations: [],
    player: { id: 'player', name: playerName, ...playerHand, score: 0 },
    ai: { id: 'ai', name: 'The Dealer', ...aiHand, score: 0, difficulty },
    lastAction: null,
  };
}

export function playToFoundation(state, sourcePlayer, cardIndex, sourceType) {
  const newState = deepClone(state);
  const player = newState[sourcePlayer];
  let card;
  if (sourceType === 'pouncePile') card = player.pouncePile[player.pouncePile.length - 1];
  else if (sourceType === 'tableau') {
    const stack = player.tableau[cardIndex] || [];
    card = stack[stack.length - 1]; // Only top card goes to foundation
  }
  else if (sourceType === 'drawDiscard') card = player.drawDiscard[player.drawDiscard.length - 1];
  if (!card) return null;

  let foundationIndex = -1;
  for (let i = 0; i < newState.foundations.length; i++) {
    const topCard = newState.foundations[i][newState.foundations[i].length - 1];
    if (canPlayOnFoundation(card, topCard)) { foundationIndex = i; break; }
  }
  if (foundationIndex === -1 && card.value === 'A') {
    newState.foundations.push([]);
    foundationIndex = newState.foundations.length - 1;
  }
  if (foundationIndex === -1) return null;

  newState.foundations[foundationIndex].push({ ...card, faceUp: true });

  if (sourceType === 'pouncePile') {
    player.pouncePile.pop();
    if (player.pouncePile.length > 0) player.pouncePile[player.pouncePile.length - 1].faceUp = true;
  } else if (sourceType === 'tableau') {
    player.tableau[cardIndex] = (player.tableau[cardIndex] || []).slice(0, -1);
    // Auto-fill empty tableau spot from pounce pile
    if (player.tableau[cardIndex].length === 0 && player.pouncePile.length > 0) {
      const fillCard = player.pouncePile.pop();
      fillCard.faceUp = true;
      player.tableau[cardIndex] = [fillCard];
      if (player.pouncePile.length > 0) player.pouncePile[player.pouncePile.length - 1].faceUp = true;
    }
  } else if (sourceType === 'drawDiscard') {
    player.drawDiscard.pop();
  }

  if (player.pouncePile.length === 0) { newState.phase = 'pounce'; newState.winner = sourcePlayer; }
  newState.lastAction = { player: sourcePlayer, type: 'foundation', card };
  return newState;
}

export function moveOnTableau(state, sourcePlayer, fromIndex, toIndex, fromSource, moveWholeStack) {
  const newState = deepClone(state);
  const player = newState[sourcePlayer];

  const targetStack = player.tableau[toIndex] || [];
  const targetTopCard = targetStack[targetStack.length - 1] || null;

  if (fromSource === 'pouncePile') {
    const card = player.pouncePile[player.pouncePile.length - 1];
    if (!card) return null;
    // Empty tableau spot only filled from pounce pile - always valid
    if (targetStack.length === 0 || canPlayOnTableau(card, targetTopCard)) {
      player.tableau[toIndex] = [...targetStack, { ...card, faceUp: true }];
      player.pouncePile.pop();
      if (player.pouncePile.length > 0) player.pouncePile[player.pouncePile.length - 1].faceUp = true;
      newState.lastAction = { player: sourcePlayer, card, type: 'tableau' };
      return newState;
    }
    return null;
  }

  if (fromSource === 'drawDiscard') {
    const card = player.drawDiscard[player.drawDiscard.length - 1];
    if (!card) return null;
    if (targetStack.length === 0) return null; // Empty spot only from pounce
    if (!canPlayOnTableau(card, targetTopCard)) return null;
    player.tableau[toIndex] = [...targetStack, { ...card, faceUp: true }];
    player.drawDiscard.pop();
    newState.lastAction = { player: sourcePlayer, card, type: 'tableau' };
    return newState;
  }

  // Moving from tableau
  const fromStack = player.tableau[fromIndex] || [];
  if (fromStack.length === 0) return null;

  if (moveWholeStack) {
    // Move entire stack - bottom card must play legally on target top
    const bottomCard = fromStack[0];
    if (targetStack.length === 0) return null; // Empty spot only from pounce
    if (!canPlayOnTableau(bottomCard, targetTopCard)) return null;
    player.tableau[toIndex] = [...targetStack, ...fromStack];
    player.tableau[fromIndex] = [];
    // Auto-fill empty spot from pounce pile
    if (player.pouncePile.length > 0) {
      const fillCard = player.pouncePile.pop();
      fillCard.faceUp = true;
      player.tableau[fromIndex] = [fillCard];
      if (player.pouncePile.length > 0) player.pouncePile[player.pouncePile.length - 1].faceUp = true;
    }
  } else {
    // Move only top card
    const topCard = fromStack[fromStack.length - 1];
    if (targetStack.length === 0) return null; // Empty spot only from pounce
    if (!canPlayOnTableau(topCard, targetTopCard)) return null;
    player.tableau[toIndex] = [...targetStack, { ...topCard, faceUp: true }];
    player.tableau[fromIndex] = fromStack.slice(0, -1);
    // Auto-fill empty spot from pounce pile
    if (player.tableau[fromIndex].length === 0 && player.pouncePile.length > 0) {
      const fillCard = player.pouncePile.pop();
      fillCard.faceUp = true;
      player.tableau[fromIndex] = [fillCard];
      if (player.pouncePile.length > 0) player.pouncePile[player.pouncePile.length - 1].faceUp = true;
    }
  }

  newState.lastAction = { player: sourcePlayer, type: 'tableau' };
  return newState;
}

export function drawCards(state, sourcePlayer) {
  const newState = deepClone(state);
  const player = newState[sourcePlayer];
  // Auto loop - if draw pile empty, recycle discard
  if (player.drawPile.length === 0) {
    if (player.drawDiscard.length === 0) return null;
    player.drawPile = [...player.drawDiscard].reverse().map(c => ({ ...c, faceUp: false }));
    player.drawDiscard = [];
  }
  const drawCount = Math.min(3, player.drawPile.length);
  for (let i = 0; i < drawCount; i++) {
    const card = player.drawPile.pop();
    card.faceUp = true;
    player.drawDiscard.push(card);
  }
  return newState;
}

export function calculateScores(state) {
  const newState = deepClone(state);
  let playerCards = 0;
  let aiCards = 0;
  for (const pile of newState.foundations) {
    for (const card of pile) {
      if (card.id.endsWith('-0')) playerCards++;
      else aiCards++;
    }
  }
  newState.player.score = playerCards - (newState.player.pouncePile.length * 2);
  newState.ai.score = aiCards - (newState.ai.pouncePile.length * 2);
  newState.phase = 'roundOver';
  return newState;
}

export function recycleDrawPile(state, sourcePlayer) {
  const newState = deepClone(state);
  const player = newState[sourcePlayer];
  if (player.drawPile.length === 0) return null;
  // Shuffle the draw pile randomly
  for (let i = player.drawPile.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [player.drawPile[i], player.drawPile[j]] = [player.drawPile[j], player.drawPile[i]];
  }
  // Draw top card to discard
  const card = player.drawPile.pop();
  card.faceUp = true;
  player.drawDiscard.push(card);
  return newState;
}
